"use client";

import { Loader2, MinusIcon, PlusIcon, ShoppingCart, Trash2Icon } from "lucide-react";
import { useEffect } from "react";
import { useProductQuantity } from "@/hooks/useProductQuantity";
import Image from "next/image";
import { Button } from "@/components/ui/button";

import { Button as AtomButton } from "@/components/atoms/buttons";
import { __, _x } from "@/lib/i18n/functions";
import { Locales } from "@/lib/i18n";
import { useCart } from "@/hooks/useCart";
import { useAddToCart, useRemoveCartItem, useUpdateCartItem } from "@/hooks/useCartMutations";
import { Toast, toast } from "@/components/atoms/toast";
import ProductBadge from "@/components/atoms/product-badge";
import { formatProductQuantity } from "@/lib/product-quantity-label";
import { formatPrice, isSaleProduct } from "@/lib/utils";
import { useWishlist } from "@/hooks/useWishlist";
import ProductConfigurationModal from "@/components/modules/account/product-configuration-modal";
import { ProductDocument } from "@/prismicio-types";
import { StoreProduct } from "@medusajs/types";
import { Badge } from "@/components/ui/badge";
import {
  getCartQuantityStep,
  normalizeConditioning,
  toLotQuantity,
  toPiecePrice,
} from "@/lib/product-units";

type InspirationProductMobileProps = {
  productName: string;
  productPrice: string;
  productImage: string;
  alt: string;
  locale: Locales;
  isLoading?: boolean;
  medusaVariantId: string;
  onQuantityChange?: (quantity: number) => void;
  productConditioning: number | string;
  price: number;
  variant?: "default" | "wishlist";
  productId?: string;
  isFullImage?: boolean;
  isOutOfStock?: boolean;
  isRange?: boolean;
  prismicProduct?: ProductDocument;
  medusaProduct?: StoreProduct;
  isPriceDynamic?: boolean;
};

const InspirationProductMobile = ({
  productName,
  alt,
  productPrice,
  productImage,
  locale,
  isLoading,
  medusaVariantId,
  onQuantityChange,
  productConditioning,
  price,
  variant = "default",
  productId,
  isFullImage = false,
  isOutOfStock = false,
  isRange = false,
  prismicProduct,
  medusaProduct,
  isPriceDynamic = false,
}: InspirationProductMobileProps) => {
  const { cart } = useCart();
  const quantityStep = getCartQuantityStep(productConditioning);
  const physicalPieceCount = normalizeConditioning(productConditioning);
  const cartItem = cart?.items && cart?.items.find((item) => item.variant_id === medusaVariantId);
  const isInCart = Boolean(cartItem);
  const cartQuantity = toLotQuantity(cartItem?.quantity || 0, productConditioning).toString();
  const { activeListId, removeFromList } = useWishlist();

  const [count, setCount] = useProductQuantity(productConditioning, cartItem?.quantity);

  useEffect(() => {
    if (cartItem?.quantity !== undefined) {
      setCount(cartItem.quantity);
    }
  }, [cartItem?.quantity, setCount]);

  const { addToCart, isLoading: isAddingToCart } = useAddToCart({
    onSuccess: () => {
      toast.success({ title: __("Item added to cart!", locale) });
    },
    onError: (error) => {
      console.error("Failed to add to cart:", error);
      toast.error({ title: __("Failed to add item to cart", locale) });
    },
  });

  const { removeItem, isLoading: isRemoving } = useRemoveCartItem({
    onSuccess: () => {
      toast.success({ title: __("Item removed from cart!", locale) });
      setCount(quantityStep);
      onQuantityChange?.(quantityStep);
    },
    onError: (error) => {
      console.error("Error removing item from cart:", error, cartItem);
      toast.error({ title: __("Error removing item from cart", locale) });
    },
  });

  const { updateQuantity } = useUpdateCartItem({
    onError: (err) => {
      console.error("Error updating quantity", err);
      toast.error({ title: __("Failed to update quantity", locale) });
    },
  });

  const handleQuantityChange = (newQuantity: number) => {
    if (cartItem?.id && newQuantity !== cartItem.quantity) {
      updateQuantity(cartItem.id, newQuantity);
    }
  };

  const handleDecrease = () => {
    const newCount = Math.max(quantityStep, count - quantityStep);
    setCount(newCount);
    onQuantityChange?.(newCount);
    handleQuantityChange(newCount);
  };

  const handleIncrease = () => {
    const newCount = count + quantityStep;
    setCount(newCount);
    onQuantityChange?.(newCount);
    handleQuantityChange(newCount);
  };

  const handleAddToCart = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    addToCart(medusaVariantId, count);
  };

  const handleDelete = () => {
    if (activeListId && productId && variant === "wishlist") {
      removeFromList(activeListId, productId);

      toast.success({ title: __("Product removed from wishlist", locale) });
    }
    removeItem(cartItem?.id || "");
  };
  const pricePerPiece = toPiecePrice(price, productConditioning);

  return (
    <article className="flex w-full items-center gap-[23px]" aria-label={__("Product item", locale)}>
      <figure className="bg-product-card relative flex size-[6.875rem] shrink-0 items-center justify-center overflow-hidden rounded-lg border-none">
        {productImage &&
          (isFullImage ? (
            <Image className="absolute object-cover mix-blend-multiply" alt={alt || ""} fill src={productImage} />
          ) : (
            <div className="relative size-20">
              <Image className="absolute object-contain mix-blend-multiply" alt={alt || ""} fill src={productImage} />
            </div>
          ))}
        {isSaleProduct(medusaProduct) && (
          <Badge className="absolute top-2 -left-6 w-[90px] -rotate-45 rounded-none" variant="festi">
            {__("To sell", locale)}
          </Badge>
        )}
      </figure>

      <div className="flex w-full flex-col items-start justify-center gap-1">
        <div className="flex w-full flex-col items-start justify-center gap-1">
          {productName && (
            <h5 className="text-blue-dark w-full text-lg leading-5 font-normal break-words whitespace-pre-wrap max-sm:text-base">
              {productName}
            </h5>
          )}

          {(isRange || isPriceDynamic) && (
            <div className="flex w-full items-center justify-between gap-1">
              <Toast
                id="product-hero"
                type="info"
                className="min-[384px]:min-w-none h-fit w-fit max-w-none py-2 shadow-none"
                title={__("Price on demand", locale)}
              />
              {isRange && medusaProduct && prismicProduct && (
                <ProductConfigurationModal
                  products={[prismicProduct]}
                  onlyIcon
                  medusaProducts={{ [medusaProduct.id]: medusaProduct }}
                  locale={locale}
                />
              )}
            </div>
          )}

          {!isRange && !isPriceDynamic && (
            <dl className="w-full items-center justify-between gap-1.25">
              <div className="flex items-center justify-between gap-2">
                <dt className="sr-only"></dt>
                {productPrice && (
                  <dd className="text-dark-primary w-full text-sm leading-3.5 font-semibold">
                    {isLoading ? <span className="shimmer inline-block h-4 w-12 rounded" /> : productPrice}
                  </dd>
                )}

                {!isLoading && (
                  <div className="flex min-w-0 items-center gap-1">
                    {physicalPieceCount > 1 && (
                      <ProductBadge className="max-w-full whitespace-normal text-right"
                        text={formatProductQuantity(count, productConditioning, locale)}
                      />
                    )}
                    {!isOutOfStock &&
                      (!isInCart ? (
                        <AtomButton
                          variant="primary"
                          onClick={handleAddToCart}
                          disabled={isLoading || isAddingToCart}
                          size="sm"
                        >
                          {isAddingToCart ? <Loader2 className="animate-spin" /> : __("Add", locale)}
                        </AtomButton>
                      ) : (
                        <div className="flex shrink-0 items-center gap-1 text-sm">
                          <span className="flex items-center gap-1 font-medium text-green-600">
                            <ShoppingCart size={16} /> ( {cartQuantity} )
                          </span>
                        </div>
                      ))}
                  </div>
                )}
              </div>
              <dt className="sr-only">{__("Price per piece", locale)}</dt>

              <dd className="text-primary-light flex-1 text-xs leading-3.5 font-normal">
                {isLoading ? (
                  <span className="shimmer inline-block h-2 w-14 rounded" />
                ) : (
                  <>
                    {physicalPieceCount > 1 && (
                      <>
                        {formatPrice(pricePerPiece, {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })}
                        /{__("piece", locale)}
                      </>
                    )}
                  </>
                )}
              </dd>
            </dl>
          )}
          {isOutOfStock && (
            <Toast
              id={`inspiration-${medusaVariantId}`}
              type="info"
              className="min-[384px]:min-w-none mt-1 w-full max-w-none shadow-none max-sm:p-2"
              titleClassName="max-sm:text-[10px]"
              title={__("This product is out of stock", locale)}
            />
          )}

          {!isLoading && !isOutOfStock && (
            <div className="flex w-full items-center justify-between gap-2.5">
              <div className="flex w-full items-center gap-1.25">
                <Button
                  onClick={handleDecrease}
                  variant="outline"
                  size="icon"
                  className="border-primary-light/23 size-7.5 cursor-pointer rounded-full bg-white p-0"
                  aria-label={`${_x("Decrease quantity of {{ productName }}", { productName }, locale)}`}
                  disabled={count <= quantityStep}
                >
                  <MinusIcon size={18} />
                </Button>

                <div
                  role="status"
                  aria-live="polite"
                  aria-atomic="true"
                  className="border-primary-light/23 flex h-7.5 w-full max-w-[7.125rem] flex-1 flex-shrink-0 items-center justify-center rounded-lg border bg-white"
                >
                  <span className="text-dark-primary flex w-full items-center justify-center text-center text-base font-semibold tabular-nums">
                    {toLotQuantity(count, productConditioning)}
                  </span>
                </div>

                <Button
                  onClick={handleIncrease}
                  variant="outline"
                  size="icon"
                  className="border-primary-light/23 size-7.5 cursor-pointer rounded-full bg-white p-0"
                  aria-label={_x("Increase quantity of {{ productName }}", { productName }, locale)}
                >
                  <PlusIcon size={18} />
                </Button>
              </div>
              <button
                disabled={!isInCart || isRemoving}
                onClick={handleDelete}
                aria-label={_x("Remove {{ productName }} from cart", { productName }, locale)}
                className="group focus:outline-pink-primary -ml-1 size-7 rounded-full p-1 disabled:cursor-not-allowed disabled:opacity-50"
              >
                <Trash2Icon
                  size={20}
                  aria-hidden="true"
                  className="text-primary-light disabled:hover:text-primary-light group-focus:text-pink-primary hover:text-pink-primary transition-all duration-200 hover:cursor-pointer"
                />
              </button>
            </div>
          )}
        </div>
      </div>
    </article>
  );
};

export default InspirationProductMobile;
